
"use client";

import { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import { getStorageItem, setStorageItem, removeStorageItem } from '@/utils/storage';

const STORAGE_KEY = 'vehicleSelection';

const defaultVehicleContext = {
  selection: null,
  setSelection: () => { },
  clearSelection: () => { },
  isActive: false,
  summary: '',
};

const VehicleContext = createContext(defaultVehicleContext);

const buildSummary = (selection) => {
  if (!selection) return '';
  const parts = [
    selection.brandName,
    selection.modelName,
    selection.yearLabel,
  ].filter(Boolean);
  const variantSummary = Array.isArray(selection.vehicleLabels) && selection.vehicleLabels.length
    ? selection.vehicleLabels.join(', ')
    : '';
  if (variantSummary) parts.push(variantSummary);
  return parts.join(' › ');
};

const readSelection = () => {
  if (typeof window === 'undefined') return null;
  try {
    const parsed = getStorageItem(STORAGE_KEY);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeSelection = (selection) => {
  if (!selection) {
    removeStorageItem(STORAGE_KEY);
    return;
  }
  setStorageItem(STORAGE_KEY, selection);
};

// The selection lives in a browser-only cookie, so it is read through an external store:
// the server snapshot is always null, which keeps hydration consistent, and the client
// switches to the saved selection right after hydrating.
const listeners = new Set();
let snapshotKey;
let snapshotValue = null;

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => {
  const value = readSelection();
  const key = value ? JSON.stringify(value) : '';
  // Same object while the stored value is unchanged, as useSyncExternalStore requires.
  if (key !== snapshotKey) {
    snapshotKey = key;
    snapshotValue = value;
  }
  return snapshotValue;
};

const getServerSnapshot = () => null;

const setSelectionState = (selection) => {
  writeSelection(selection);
  listeners.forEach((listener) => listener());
};

export const VehicleProvider = ({ children }) => {
  const selection = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setSelection = useCallback((next) => {
    const normalized = next && typeof next === 'object' ? {
      brandId: next.brandId || '',
      brandName: next.brandName || '',
      modelId: next.modelId || '',
      modelName: next.modelName || '',
      yearId: next.yearId || '',
      yearLabel: next.yearLabel || '',
      vehicleIds: Array.isArray(next.vehicleIds) ? next.vehicleIds : [],
      vehicleLabels: Array.isArray(next.vehicleLabels) ? next.vehicleLabels : [],
    } : null;
    setSelectionState(normalized);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectionState(null);
  }, []);

  const value = useMemo(() => {
    const active = Boolean(selection?.vehicleIds?.length);
    return {
      selection,
      setSelection,
      clearSelection,
      isActive: active,
      summary: buildSummary(selection),
    };
  }, [selection, setSelection, clearSelection]);

  return (
    <VehicleContext.Provider value={value}>
      {children}
    </VehicleContext.Provider>
  );
};

export const useVehicleSelection = () => {
  const ctx = useContext(VehicleContext);
  return ctx || defaultVehicleContext;
};
